import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import { apiService } from './api';

// Dynamic imports for Firebase (only on native platforms)
let messaging: any = null;
let PermissionsAndroid: any = null;

// Async function to load Firebase only on native
const loadFirebase = async () => {
  if (Platform.OS !== 'web' && !messaging) {
    try {
      const firebaseMessaging = await import('@react-native-firebase/messaging');
      messaging = firebaseMessaging.default;
    } catch (error) {
      console.warn('[FCMService] Firebase messaging not available:', error);
    }
  }
};

// Load PermissionsAndroid only on Android
const loadPermissions = async () => {
  if (Platform.OS === 'android' && !PermissionsAndroid) {
    try {
      const RN = await import('react-native');
      PermissionsAndroid = RN.PermissionsAndroid;
    } catch (error) {
      console.warn('[FCMService] PermissionsAndroid not available:', error);
    }
  }
};

export interface FCMTokenData {
  fcmToken: string;
  deviceType: 'android' | 'ios' | 'web';
  deviceInfo: {
    model?: string;
    os?: string;
    appVersion?: string;
    [key: string]: any;
  };
}

export interface FCMNotification {
  _id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, string>;
  clickAction?: string;
  priority: string;
  createdAt: string;
  read: boolean;
}

// Configure how notifications are displayed
// Remote pushes are NOT shown while the app is in the foreground: the
// socket's in-app notification already covers that state, and showing both
// (plus the old onMessage local copy) produced duplicates. Locally scheduled
// notifications (reminders etc.) still show. In background/closed states iOS
// displays the push itself — this handler isn't involved there.
Notifications.setNotificationHandler({
  handleNotification: async (notification: any) => {
    const isRemotePush = notification?.request?.trigger?.type === 'push';
    return {
      shouldShowAlert: !isRemotePush,
      shouldShowBanner: !isRemotePush,
      shouldShowList: !isRemotePush,
      shouldPlaySound: !isRemotePush,
      shouldSetBadge: true,
    } as any;
  },
});

export class FCMService {
  private static instance: FCMService;
  private isInitialized: boolean = false;
  private isInitializing: boolean = false;
  // Shared by concurrent initialize() callers — previously a second caller
  // (e.g. the post-login registerToken path) returned immediately while the
  // first was still waiting on iOS's APNs/FCM token, then registerToken saw
  // no token and gave up for the session.
  private initPromise: Promise<void> | null = null;
  private tokenRefreshUnsubscribe: (() => void) | null = null;
  // Step-by-step push status, shown in Settings → Notifications so a
  // TestFlight/release build (no console access) can tell exactly which
  // step fails: permission, APNs token, FCM token, or server registration.
  diagnostics: {
    permission?: string;
    apnsToken?: boolean;
    fcmToken?: string;
    initError?: string;
    lastRegistration?: { at: string; ok: boolean; statusCode?: number; error?: string };
    lastTest?: { at: string; ok: boolean; statusCode?: number; detail?: string };
  } = {};
  private currentToken: string | null = null;
  private currentUserId: string | null = null; // Track which user the token is registered to
  private navigationHandler: ((notification: FCMNotification) => void) | null = null;

  private constructor() {
    // Auto-initialize on construction (non-blocking)
    if (Platform.OS !== 'web') {
      console.log('🔔 [FCMService] Constructor called, will auto-initialize FCM');
      // Use setTimeout to make it non-blocking
      setTimeout(() => {
        this.initialize().catch(error => {
          console.error('❌ [FCMService] Auto-initialization failed:', error);
        });
      }, 100);
    }
  }

  static getInstance(): FCMService {
    if (!FCMService.instance) {
      FCMService.instance = new FCMService();
      console.log('✅ [FCMService] Singleton instance created');
    }
    return FCMService.instance;
  }

  /**
   * Set navigation handler for notification taps
   */
  setNavigationHandler(handler: (notification: FCMNotification) => void): void {
    this.navigationHandler = handler;
    console.log('✅ [FCMService] Navigation handler set');
  }

  /**
   * Initialize FCM service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this.doInitialize().finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  private async doInitialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('🔔 [FCMService] Already initialized, skipping');
      return;
    }

    if (this.isInitializing) {
      console.log('🔔 [FCMService] Already initializing, skipping duplicate call');
      return;
    }

    this.isInitializing = true;

    try {
      console.log('🔔 [FCMService] ===== STARTING FCM INITIALIZATION =====');
      console.log('🔔 [FCMService] Platform:', Platform.OS);
      
      // Check if Firebase is available
      if (Platform.OS === 'web') {
        console.log('🔔 [FCMService] Web platform - using Expo Notifications only');
        await this.initializeExpoNotifications();
        this.isInitializing = false;
        return;
      }

      console.log('🔔 [FCMService] Initializing Firebase Cloud Messaging for', Platform.OS);
      console.log('🔔 [FCMService] Firebase messaging object available:', !!messaging);
      
      // Check if Firebase messaging is available
      if (!messaging) {
        console.error('❌ [FCMService] Firebase messaging NOT available - attempting to load dynamically...');
        
        // Try to load Firebase dynamically as a last resort
        try {
          const firebaseModule = require('@react-native-firebase/messaging');
          messaging = firebaseModule.default;
          console.log('✅ [FCMService] Firebase loaded dynamically!');
        } catch (dynamicError) {
          console.error('❌ [FCMService] Failed to load Firebase dynamically:', dynamicError);
          console.error('❌ [FCMService] FCM will NOT work. Firebase module is not available in this build.');
          this.isInitialized = true; // Mark as initialized to prevent retry loops
          this.isInitializing = false;
          return;
        }
      }
      
      console.log('🔔 [FCMService] Requesting notification permission...');
      
      // For Android 13+ (API level 33+), request POST_NOTIFICATIONS permission first
      if (Platform.OS === 'android' && Platform.Version >= 33 && PermissionsAndroid) {
        try {
          console.log('🔔 [FCMService] Android 13+ detected, requesting POST_NOTIFICATIONS permission...');
          const granted = await PermissionsAndroid.request(
            PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS,
            {
              title: 'Notification Permission',
              message: 'DISLIX needs notification permission to alert you about new messages',
              buttonNeutral: 'Ask Me Later',
              buttonNegative: 'Cancel',
              buttonPositive: 'OK',
            }
          );
          
          if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
            console.error('❌ [FCMService] Android POST_NOTIFICATIONS permission DENIED');
            this.isInitialized = true;
            this.isInitializing = false;
            return;
          }
          
          console.log('✅ [FCMService] Android POST_NOTIFICATIONS permission GRANTED');
        } catch (err) {
          console.error('❌ [FCMService] Error requesting Android permission:', err);
        }
      }
      
      // Request Firebase permission (iOS and Android < 13)
      const authStatus = await messaging().requestPermission();
      this.diagnostics.permission = String(authStatus);
      console.log('🔔 [FCMService] Firebase permission status:', authStatus);
      
      const enabled =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL;

      if (!enabled) {
        console.error('❌ [FCMService] Notification permission DENIED by user');
        this.isInitialized = true; // Mark as initialized even if permission denied
        this.isInitializing = false;
        return;
      }

      console.log('✅ [FCMService] Notification permission GRANTED');
      
      // Create notification channel for Android with sound and vibration using Expo Notifications
      if (Platform.OS === 'android') {
        try {
          console.log('🔔 [FCMService] Creating notification channel for Android...');
          const Notifications = require('expo-notifications');
          
          await Notifications.setNotificationChannelAsync('default', {
            name: 'Default Notifications',
            importance: Notifications.AndroidImportance.HIGH,
            vibrationPattern: [0, 250, 250, 250],
            lightColor: '#FF0000',
            sound: 'default',
            enableVibrate: true,
            enableLights: true,
            showBadge: true,
          });
          
          console.log('✅ [FCMService] Notification channel created with sound and vibration');
        } catch (channelError) {
          console.warn('⚠️ [FCMService] Could not create notification channel:', channelError);
          console.log('🔔 [FCMService] Continuing without custom channel - using system defaults');
        }
      }
      
      console.log('🔔 [FCMService] Getting FCM token from Firebase...');

      // iOS: register with APNs explicitly before asking for the FCM token
      // (FCM can only mint an iOS token once APNs has issued one).
      if (Platform.OS === 'ios') {
        try {
          await messaging().registerDeviceForRemoteMessages();
        } catch (apnsError) {
          console.warn('⚠️ [FCMService] registerDeviceForRemoteMessages failed:', apnsError);
        }
      }

      if (Platform.OS === 'ios') {
        try {
          this.diagnostics.apnsToken = !!(await messaging().getAPNSToken());
        } catch {
          this.diagnostics.apnsToken = false;
        }
      }

      // Get FCM token — on iOS getToken() waits for the APNs token and can
      // hang indefinitely (no error) when APNs never delivers one (missing
      // push entitlement in the provisioning profile, Simulator, no network).
      // Time out so the diagnostics show which step stalled.
      const token: string = await Promise.race([
        messaging().getToken(),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error(
            Platform.OS === 'ios' && !this.diagnostics.apnsToken
              ? 'Timed out waiting for FCM token — iOS never issued an APNs token (check Push Notifications capability/provisioning profile)'
              : 'Timed out waiting for FCM token'
          )), 20000)
        ),
      ]);
      this.currentToken = token;
      this.diagnostics.fcmToken = token ? `${token.slice(0, 12)}…` : undefined;

      // Firebase rotates tokens (reinstall, restore, periodic refresh) —
      // re-register so the server never keeps pushing to a dead token.
      if (!this.tokenRefreshUnsubscribe) {
        this.tokenRefreshUnsubscribe = messaging().onTokenRefresh(async (refreshedToken: string) => {
          this.currentToken = refreshedToken;
          try {
            await this.registerToken();
          } catch (refreshError) {
            console.warn('⚠️ [FCMService] Re-registering refreshed token failed:', refreshError);
          }
        });
      }
      console.log('✅ [FCMService] FCM token obtained successfully!');
      console.log('🔔 [FCMService] Token preview:', token ? token.substring(0, 30) + '...' : 'NULL');

      // Setup notification handlers
      this.setupNotificationHandlers();

      this.isInitialized = true;
      this.isInitializing = false;
      console.log('✅ [FCMService] ===== FCM SERVICE INITIALIZED SUCCESSFULLY =====');
    } catch (error) {
      this.diagnostics.initError = (error as any)?.message || String(error);
      console.error('❌ [FCMService] ===== FCM INITIALIZATION FAILED =====');
      console.error('❌ [FCMService] Error details:', error);
      console.error('❌ [FCMService] Error message:', error.message);
      console.error('❌ [FCMService] Error stack:', error.stack);
      this.isInitialized = true; // Mark as initialized to prevent retry loops
      this.isInitializing = false;
    }
  }

  /**
   * Initialize Expo Notifications (for web/development)
   */
  private async initializeExpoNotifications(): Promise<void> {
    try {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      
      if (finalStatus !== 'granted') {
        console.warn('🔔 [FCMService] Expo notification permission denied');
        return;
      }

      const token = (await Notifications.getExpoPushTokenAsync()).data;
      this.currentToken = token;
      console.log('✅ [FCMService] Expo push token obtained');

      this.isInitialized = true;
    } catch (error) {
      console.error('❌ [FCMService] Error initializing Expo notifications:', error);
    }
  }

  /**
   * Setup notification handlers
   */
  private setupNotificationHandlers(): void {
    console.log('🔔 [FCMService] Setting up notification handlers');

    if (!messaging) {
      console.warn('🔔 [FCMService] Cannot setup handlers - Firebase messaging not available');
      return;
    }

    // Foreground messages
    messaging().onMessage(async (_remoteMessage: any) => {
      // Foreground: intentionally not displayed — the socket's in-app
      // notification shows it (a local copy here was a duplicate).
    });

    // Notification tapped (app opened from notification)
    messaging().onNotificationOpenedApp((remoteMessage: any) => {
      console.log('🔔 [FCMService] Notification tapped (app in background):', remoteMessage);
      this.handleNotificationTap(remoteMessage);
    });

    // App opened from quit state by notification
    messaging()
      .getInitialNotification()
      .then((remoteMessage: any) => {
        if (remoteMessage) {
          console.log('🔔 [FCMService] App opened from notification (cold start):', remoteMessage);
          this.handleNotificationTap(remoteMessage);
        }
      });

    console.log('✅ [FCMService] Notification handlers configured');
  }

  /**
   * Show local notification for foreground messages
   */
  private async showLocalNotification(remoteMessage: any): Promise<void> {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: remoteMessage.notification?.title || 'New Notification',
          body: remoteMessage.notification?.body || '',
          data: remoteMessage.data,
          sound: true,
        },
        trigger: null, // Show immediately
      });
    } catch (error) {
      console.error('❌ [FCMService] Error showing local notification:', error);
    }
  }

  /**
   * Handle notification tap
   */
  private async handleNotificationTap(remoteMessage: any): Promise<void> {
    try {
      const data = remoteMessage.data;
      console.log('🔔 [FCMService] Handling notification tap with data:', data);

      // Mark notification as read in backend
      const notificationId = data.notificationId;
      if (notificationId) {
        try {
          console.log('📝 [FCMService] Marking notification as read:', notificationId);
          await apiService.authenticatedRequest(
            `/api/notifications/${notificationId}/read`,
            { method: 'PUT' }
          );
          console.log('✅ [FCMService] Notification marked as read');
        } catch (error) {
          console.error('❌ [FCMService] Failed to mark notification as read:', error);
        }
      }

      if (this.navigationHandler) {
        const notification: FCMNotification = {
          _id: data.notificationId || '',
          type: data.notificationType || data.type || '',
          title: remoteMessage.notification?.title || '',
          body: remoteMessage.notification?.body || '',
          data: data,
          clickAction: data.clickAction,
          priority: data.priority || 'normal',
          createdAt: data.timestamp || new Date().toISOString(),
          read: true // Mark as read since user clicked it
        };

        this.navigationHandler(notification);
      } else {
        console.warn('⚠️ [FCMService] No navigation handler set');
      }
    } catch (error) {
      console.error('❌ [FCMService] Error handling notification tap:', error);
    }
  }

  /**
   * Register FCM token with the API
   * @param onAuthError - Callback to handle authentication errors (e.g., redirect to login)
   * @param forceReregister - Force re-registration even if already registered (for user switching)
   */
  async registerToken(onAuthError?: () => void, forceReregister: boolean = false): Promise<boolean> {
    try {
      console.log('🔔 [FCMService] ===== ATTEMPTING FCM TOKEN REGISTRATION =====');
      console.log('🔔 [FCMService] Current token exists:', !!this.currentToken);
      console.log('🔔 [FCMService] Is initialized:', this.isInitialized);
      console.log('🔔 [FCMService] Force re-register:', forceReregister);
      
      // Called right after login, possibly while initialize() is still
      // waiting on the token — wait for it instead of giving up.
      if (!this.currentToken) {
        await this.initialize();
      }
      if (!this.currentToken && Platform.OS !== 'web' && messaging) {
        try {
          this.currentToken = await messaging().getToken();
        } catch (tokenError) {
          console.warn('⚠️ [FCMService] getToken retry failed:', tokenError);
        }
      }

      if (!this.currentToken) {
        this.diagnostics.lastRegistration = { at: new Date().toISOString(), ok: false, error: 'No FCM token on this device' };
        console.error('❌ [FCMService] No FCM token available to register!');
        console.error('❌ [FCMService] This means FCM initialization failed or is incomplete.');
        console.error('❌ [FCMService] Check if Firebase is properly included in the APK.');
        return false;
      }

      // Get current user ID from API service or auth token
      const currentUserId = await this.getCurrentUserId();
      console.log('🔔 [FCMService] Current user ID:', currentUserId);
      console.log('🔔 [FCMService] Previously registered to user:', this.currentUserId);

      // Always register token to support:
      // 1. Same user on multiple devices (each device has unique token)
      // 2. Multiple users on same device (token reassignment)
      // 3. Token refresh/updates
      if (this.currentUserId && this.currentUserId !== currentUserId) {
        console.log('🔄 [FCMService] User changed! Re-registering token for new user...');
        console.log('🔄 [FCMService] Old user:', this.currentUserId);
        console.log('🔄 [FCMService] New user:', currentUserId);
      } else {
        console.log('🔄 [FCMService] Registering/updating token for user:', currentUserId);
      }

      console.log('📤 [FCMService] Registering FCM token with backend...');
      console.log('🔔 [FCMService] Token preview:', this.currentToken.substring(0, 30) + '...');

      // deviceId (Keychain/SecureStore-backed, survives app updates) lets the
      // server retire this device's older tokens — stale tokens from earlier
      // installs/builds were each still delivered, duplicating every push.
      const { getDeviceId } = await import('../utils/deviceId');
      const Constants = (await import('expo-constants')).default;
      const deviceInfo = {
        model: Platform.OS,
        os: Platform.OS,
        appVersion: Constants.expoConfig?.version || 'unknown',
        deviceId: await getDeviceId(),
      };

      console.log('🔔 [FCMService] Making API request to /api/fcm-tokens/register...');
      
      const response = await apiService.authenticatedRequest('/api/fcm-tokens/register', {
        method: 'POST',
        body: JSON.stringify({
          fcmToken: this.currentToken,
          deviceType: Platform.OS === 'android' ? 'android' : Platform.OS === 'ios' ? 'ios' : 'web',
          deviceInfo,
        }),
      });

      console.log('🔔 [FCMService] API response received:', {
        statusCode: response.statusCode,
        hasData: !!response.data,
        hasErrors: !!response.errors
      });

      this.diagnostics.lastRegistration = {
        at: new Date().toISOString(),
        ok: !!(response.data || response.statusCode === 1000),
        statusCode: response.statusCode,
        error: response.errors ? JSON.stringify(response.errors).slice(0, 300) : undefined,
      };

      if (response.statusCode === 1011 && onAuthError) {
        console.error('❌ [FCMService] Authentication error during token registration');
        onAuthError();
        return false;
      }

      if (response.data || response.statusCode === 1000) {
        console.log('✅ [FCMService] ===== FCM TOKEN REGISTERED SUCCESSFULLY =====');
        this.currentUserId = currentUserId; // Track which user the token is registered to
        return true;
      } else {
        console.error('❌ [FCMService] Failed to register token. Response:', response);
        console.error('❌ [FCMService] Errors:', response.errors);
        return false;
      }
    } catch (error) {
      console.error('❌ [FCMService] ===== ERROR REGISTERING TOKEN =====');
      console.error('❌ [FCMService] Error:', error);
      console.error('❌ [FCMService] Error message:', error.message);
      console.error('❌ [FCMService] Error stack:', error.stack);
      return false;
    }
  }

  /**
   * Get current user ID (helper method)
   */
  private async getCurrentUserId(): Promise<string | null> {
    try {
      // Try to get user from the auth token or API
      const response = await apiService.authenticatedRequest('/api/auth/me', {
        method: 'GET'
      });
      
      if (response.data?.user) {
        return response.data.user._id || response.data.user.id;
      }
      
      return null;
    } catch (error) {
      console.warn('⚠️ [FCMService] Could not get current user ID:', error);
      return null;
    }
  }

  /**
   * Unregister FCM token from the API (call on logout)
   */
  async unregisterToken(): Promise<boolean> {
    try {
      if (!this.currentToken) {
        console.warn('⚠️ [FCMService] No FCM token to unregister');
        this.currentUserId = null;
        return true;
      }

      console.log('📤 [FCMService] Unregistering FCM token from backend...');
      console.log('🔔 [FCMService] Token was registered to user:', this.currentUserId);

      const response = await apiService.authenticatedRequest('/api/fcm-tokens/unregister', {
        method: 'POST',
        body: JSON.stringify({
          fcmToken: this.currentToken,
        }),
      });

      if (response.data || response.statusCode === 1000) {
        console.log('✅ [FCMService] FCM token unregistered successfully');
        this.currentUserId = null; // Clear user tracking
        // Don't clear this.currentToken - device token stays the same
        return true;
      } else {
        console.error('❌ [FCMService] Failed to unregister token:', response.errors);
        return false;
      }
    } catch (error) {
      console.error('❌ [FCMService] Error unregistering token:', error);
      return false;
    }
  }

  /**
   * Clear user registration (call on logout without unregistering from backend)
   */
  clearUserRegistration(): void {
    console.log('🔄 [FCMService] Clearing user registration tracking');
    this.currentUserId = null;
  }

  /**
   * Get current FCM token
   */
  getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Refresh FCM token
   */
  async refreshToken(onAuthError?: () => void): Promise<string | null> {
    try {
      if (Platform.OS === 'web' || !messaging) {
        return null;
      }

      const token = await messaging().getToken();
      
      if (token !== this.currentToken) {
        console.log('🔄 [FCMService] Token refreshed');
        this.currentToken = token;
        await this.registerToken(onAuthError);
      }
      
      return token;
    } catch (error) {
      console.error('❌ [FCMService] Error refreshing token:', error);
      return null;
    }
  }

  /**
   * Send a test notification
   */
  async sendTestNotification(): Promise<boolean> {
    try {
      console.log('🧪 [FCMService] Sending test notification...');

      const response = await apiService.authenticatedRequest('/api/fcm-tokens/test', {
        method: 'POST',
        body: JSON.stringify({
          title: 'Test Notification',
          body: 'This is a test notification from DISLIX',
        }),
      });

      this.diagnostics.lastTest = {
        at: new Date().toISOString(),
        ok: !!(response.data || response.statusCode === 1000),
        statusCode: response.statusCode,
        detail: JSON.stringify(response.data ?? response.errors ?? null).slice(0, 300),
      };

      if (response.data || response.statusCode === 1000) {
        console.log('✅ [FCMService] Test notification sent successfully');
        return true;
      } else {
        console.error('❌ [FCMService] Failed to send test notification:', response.errors);
        return false;
      }
    } catch (error) {
      console.error('❌ [FCMService] Error sending test notification:', error);
      return false;
    }
  }
}

export const fcmService = FCMService.getInstance();
